import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircleHeart, Sparkles, Wind, BookHeart, TrendingUp } from "lucide-react";
import { APP_VERSION } from "@/lib/version";

const features = [
    { icon: MessageCircleHeart, title: "A companion who listens", body: "Talk to a warm AI friend when the day feels heavy. No judgment, just presence." },
    { icon: TrendingUp, title: "Gentle mood tracking", body: "A soft way to notice how you're feeling — without turning yourself into a spreadsheet." },
    { icon: Sparkles, title: "Daily affirmations", body: "A small handful of kind words waiting for you every morning." },
    { icon: Wind, title: "Breathing exercises", body: "Slow, guided breathing when your chest feels tight. Four in, hold, six out." },
    { icon: BookHeart, title: "Save what helps", body: "Keep the responses and reminders that felt like a hug. Return to them anytime." },
];

export default function Landing() {
    return (
        <div className="min-h-screen relative overflow-hidden bg-background">
            {/* Ambient blobs */}
            <div className="blob blob-peach w-[500px] h-[500px] -top-40 -left-40" />
            <div className="blob blob-cream w-[600px] h-[600px] top-1/3 -right-40" />
            <div className="blob blob-sage w-[400px] h-[400px] bottom-0 left-1/3 opacity-40" />

            {/* Nav */}
            <nav className="relative z-10 flex items-center justify-between px-6 sm:px-12 py-6">
                <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
                    <div className="w-9 h-9 rounded-2xl bg-primary/90 flex items-center justify-center shadow-soft">
                        <Heart className="w-5 h-5 text-primary-foreground" strokeWidth={2} fill="currentColor" />
                    </div>
                    <span className="font-heading text-2xl font-[800] tracking-tight">CheerUp!</span>
                </Link>
                <div className="flex items-center gap-2 sm:gap-3">
                    <Link to="/login" data-testid="nav-login">
                        <Button variant="ghost" className="rounded-full font-medium">Sign in</Button>
                    </Link>
                    <Link to="/signup" data-testid="nav-signup">
                        <Button className="rounded-full font-medium shadow-soft">Get started</Button>
                    </Link>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative z-10 px-6 sm:px-12 pt-10 lg:pt-14 pb-20">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-7xl">
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-xl border border-border shadow-soft mb-6"
                        >
                            <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                            <span className="text-sm font-medium">A soft place for hard days.</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, delay: 0.15, ease: "easeOut" }}
                            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-[800] leading-[1.05] tracking-tight mb-6"
                        >
                            When you're feeling down,
                            <span className="block text-primary italic font-[300] mt-3">we'll sit with you.</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.35, ease: "easeOut" }}
                            className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg"
                        >
                            CheerUp! is a gentle AI companion, mood journal, and calm-down toolkit — for the moments when you need someone kind to be there.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.55, ease: "easeOut" }}
                            className="flex flex-col sm:flex-row gap-4"
                        >
                            <Link to="/signup" data-testid="hero-cta-signup">
                                <Button size="lg" className="rounded-full h-14 px-8 text-base font-medium shadow-soft-lg hover:-translate-y-0.5 transition-transform duration-300">
                                    <Heart className="w-5 h-5 mr-2" strokeWidth={2} fill="currentColor" />
                                    Start feeling better
                                </Button>
                            </Link>
                            <Link to="/login" data-testid="hero-cta-login">
                                <Button size="lg" variant="outline" className="rounded-full h-14 px-8 text-base font-medium bg-white/60 backdrop-blur-xl border-border hover:bg-white/80 transition-colors duration-300">
                                    I already have an account
                                </Button>
                            </Link>
                        </motion.div>
                    </div>

                    {/* Hero image card */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2, delay: 0.4, ease: "easeOut" }}
                        className="relative"
                    >
                        <div className="rounded-[2rem] overflow-hidden shadow-soft-lg border border-border relative">
                            <img
                                src="/hero.jpg"
                                alt="A cheerful cartoon man and woman with big sparkly bubbly eyes looking up hopefully at a warm sunrise sky with soft sparkles"
                                className="w-full h-[340px] sm:h-[420px] lg:h-[520px] object-cover"
                            />
                            <div className="absolute bottom-5 left-5 sm:bottom-8 sm:left-8 max-w-md bg-white/85 backdrop-blur-xl rounded-2xl p-5 border border-border shadow-soft">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                                        <Heart className="w-4 h-4 text-primary-foreground" strokeWidth={2} fill="currentColor" />
                                    </div>
                                    <span className="font-heading font-[700]">CheerUp!</span>
                                </div>
                                <p className="text-sm leading-relaxed">Hey. Whatever you're carrying today, you don't have to carry it alone. Want to tell me what's on your heart?</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features */}
            <section className="relative z-10 px-6 sm:px-12 py-24 bg-white/40 backdrop-blur-sm">
                <div className="max-w-6xl">
                    <div className="mb-16 max-w-2xl">
                        <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4 font-medium">A gentle toolkit</p>
                        <h2 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight leading-tight">Small things that help on hard days.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((f, i) => (
                            <motion.div
                                key={f.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                className="bg-card rounded-2xl p-8 border border-border shadow-soft hover:-translate-y-1 hover:shadow-soft-lg transition-transform duration-300"
                            >
                                <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center mb-5">
                                    <f.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                                </div>
                                <h3 className="font-heading text-xl font-[700] mb-3 tracking-tight">{f.title}</h3>
                                <p className="text-muted-foreground leading-relaxed">{f.body}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative z-10 px-6 sm:px-12 py-24">
                <div className="max-w-3xl">
                    <h2 className="font-heading text-4xl sm:text-5xl font-[300] italic tracking-tight leading-tight mb-6">
                        Come as you are. <span className="not-italic font-[800]">Leave a little lighter.</span>
                    </h2>
                    <p className="text-lg text-muted-foreground mb-10 leading-relaxed">Free to start, no crisis required. Sometimes we just need a soft voice in the middle of the day.</p>
                    <Link to="/signup" data-testid="footer-cta-signup">
                        <Button size="lg" className="rounded-full h-14 px-10 text-base font-medium shadow-soft-lg">
                            Create your safe space
                        </Button>
                    </Link>
                </div>
            </section>

            <footer className="relative z-10 px-6 sm:px-12 py-10 border-t border-border">
                <div className="max-w-6xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                        <Heart className="w-4 h-4 text-primary" strokeWidth={2} fill="currentColor" />
                        <span>CheerUp! — Made with warmth.</span>
                        <span className="text-xs text-muted-foreground/70 ml-1" data-testid="app-version">v{APP_VERSION}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                        <Link to="/privacy" className="hover:text-foreground transition-colors duration-200" data-testid="footer-privacy-link">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="hover:text-foreground transition-colors duration-200" data-testid="footer-terms-link">
                            Terms of Service
                        </Link>
                        <a href="mailto:query@cheerup-ai.com" className="hover:text-foreground transition-colors duration-200" data-testid="footer-feedback-link">
                            Feedback: query@cheerup-ai.com
                        </a>
                    </div>
                </div>
                <div className="max-w-6xl text-xs text-muted-foreground mt-4">
                    Not a substitute for professional care. If you're in crisis, please call your local helpline.
                </div>
            </footer>
        </div>
    );
}
